/**
 * 고유링크 카드 등록 콜백(`/payments/toss/billing/link`)의 **결과 코드 → 화면 안내** 표. (2026-10-23)
 *
 * 서버 `TossPaymentsController.linkBillingKeyRegistration` 의 응답 `data` 는
 *  - `'success'`            이번 호출로 등록됨
 *  - `'fail'`               티켓은 통과했으나 토스가 등록을 거절함
 *  - 그 외                   티켓 거절 사유(`CardRegistrationLinkService.ConsumeFailure` 의 이름)
 *
 * 🔑 왜 표로 빼나 — 종전 화면은 `'success'` 가 아니면 전부 "등록되지 않았습니다. 링크 유효기간이
 *    지났을 수 있습니다" 였다. 그러면 **이미 등록된 고객이 새로고침하고는 다시 시도**하고,
 *    정말 만료된 고객은 **돌아갈 길이 없어** 같은 화면만 새로고침한다(2026-10-23 사용자 지적).
 *    결과마다 "끝났다 / 다시 하라 / 담당자에게 말하라" 가 달라야 한다.
 *
 * `retry`:
 *  - `'link'`   링크 화면(`/card/register?token=…`)으로 돌아가 **사업자번호 확인부터 다시** 한다.
 *               티켓은 1회용·15분이라 새 티켓이 필요한 모든 경우가 여기다.
 *  - `'reload'` 같은 콜백을 다시 부르면 결과가 정해지는 경우(동시 처리 중·통신 오류).
 *  - `null`     고객이 할 일이 없다(완료) 또는 할 수 없다(링크 잠김 → 담당자).
 */

/** 링크 화면이 결제창으로 가기 전에 토큰을 남기는 자리. 같은 탭 안에서만 산다. */
export const LINK_TOKEN_STORAGE_KEY = 'omoplay.cardLink.token';

/** 결제창으로 가기 전에 호출. 저장이 막혀 있어도(시크릿·차단) 등록 흐름은 막지 않는다. */
export function saveLinkToken(token) {
    if (!token) return;
    try {
        window.sessionStorage.setItem(LINK_TOKEN_STORAGE_KEY, token);
    } catch (e) {
        /* 저장 실패는 "다시 진행" 버튼이 안 뜨는 것으로 끝난다 — 안내문이 링크를 다시 열라고 말한다. */
    }
}

/** 콜백 화면이 "다시 진행" 버튼을 만들 때 읽는다. 없으면 null. */
export function readSavedLinkToken() {
    try {
        return window.sessionStorage.getItem(LINK_TOKEN_STORAGE_KEY) || null;
    } catch (e) {
        return null;
    }
}

/** 링크 화면 경로. 토큰이 없으면 null — 그때는 "처음 받은 링크를 다시 열어주세요" 로 안내한다. */
export function linkPagePath(token) {
    return token ? `/card/register?token=${encodeURIComponent(token)}` : null;
}

const RETRY_HINT = '아래 버튼으로 사업자번호 확인부터 다시 진행해주세요.';

/**
 * @param {string|undefined} code 서버 응답 `data`
 * @param {{setDefault?: boolean}} opts 콜백 URL 의 setDefault(성공 안내에만 쓴다)
 * @returns {{status: 'ok'|'fail', title: string, message: string, note: string, retry: 'link'|'reload'|null}}
 */
export function describeCardLinkResult(code, {setDefault = false} = {}) {
    switch (code) {
        case 'success':
            return {
                status: 'ok',
                title: '카드가 정상적으로 등록되었습니다.',
                message: '이 창은 닫으셔도 됩니다.',
                // 고객이 '네' 를 눌렀을 때만 덧붙인다. '아니오' 인데도 기본이 되는 경우가
                // 있으므로(기본카드가 없던 계정) 여기서 단정하지 않는다.
                note: setDefault ? '이 카드를 기본 결제 카드로 설정했습니다.' : '',
                retry: null,
            };
        case 'ALREADY_REGISTERED':
            return {
                status: 'ok',
                title: '카드 등록이 이미 완료되었습니다.',
                message: '이 화면이 다시 열렸지만 카드는 앞서 정상 등록되었습니다. 다시 등록하실 필요가 없습니다. 이 창은 닫으셔도 됩니다.',
                note: '',
                retry: null,
            };
        case 'ALREADY_USED':
            return {
                status: 'fail',
                title: '등록이 확인되지 않았습니다',
                message: `이 요청은 이미 한 번 처리되었지만 등록된 카드를 찾지 못했습니다. ${RETRY_HINT}`,
                note: '',
                retry: 'link',
            };
        case 'EXPIRED':
            return {
                status: 'fail',
                title: '유효 시간이 지났습니다',
                message: `사업자번호 확인 후 15분 안에 카드 등록을 마쳐야 합니다. ${RETRY_HINT}`,
                note: '',
                retry: 'link',
            };
        case 'NOT_FOUND':
            return {
                status: 'fail',
                title: '요청 정보가 일치하지 않습니다',
                message: `사업자번호를 다시 확인했거나 이전에 열었던 창의 요청일 수 있습니다. ${RETRY_HINT}`,
                note: '',
                retry: 'link',
            };
        case 'LINK_DISABLED':
            return {
                status: 'fail',
                title: '이 링크는 더 이상 사용할 수 없습니다',
                message: '새 링크가 발급되었거나 사업자번호 입력 횟수를 초과했습니다. 담당자에게 새 링크를 요청해주세요.',
                note: '',
                retry: null,
            };
        case 'RACE':
            return {
                status: 'fail',
                title: '처리 중입니다',
                message: '같은 요청이 동시에 처리되고 있습니다. 잠시 후 결과를 다시 확인해주세요.',
                note: '',
                retry: 'reload',
            };
        case 'fail':
            return {
                status: 'fail',
                title: '카드를 등록하지 못했습니다',
                message: `카드사에서 등록을 거절했을 수 있습니다. 다른 카드로 다시 진행하거나 담당자에게 문의해주세요. ${RETRY_HINT}`,
                note: '',
                retry: 'link',
            };
        default:
            return {
                status: 'fail',
                title: '등록되지 않았습니다',
                message: `카드를 등록하지 못했습니다. ${RETRY_HINT}`,
                note: '',
                retry: 'link',
            };
    }
}

/** 토스가 실패/취소를 알려온 경우(`code`·`message` 쿼리). 고객이 창을 닫았거나 카드사 거절. */
export function describeTossFailure(tossMessage) {
    return {
        status: 'fail',
        title: '카드 등록이 취소되었습니다',
        message: `${tossMessage ? `${tossMessage} ` : ''}${RETRY_HINT}`.trim(),
        note: '',
        retry: 'link',
    };
}

/** 콜백 URL 에 필요한 값이 빠진 경우(티켓·authKey·customerKey). */
export function describeMissingParams() {
    return {
        status: 'fail',
        title: '요청 정보가 올바르지 않습니다',
        message: `결제창에서 돌아온 정보가 비어 있습니다. ${RETRY_HINT}`,
        note: '',
        retry: 'link',
    };
}

/** 서버 통신 자체가 실패한 경우. 같은 콜백을 다시 부르면 서버가 상태를 알려 준다(등록됐으면 ALREADY_REGISTERED). */
export function describeNetworkError() {
    return {
        status: 'fail',
        title: '카드 등록 중 오류가 발생했습니다',
        message: '잠시 후 아래 버튼으로 결과를 다시 확인해주세요. 이미 등록된 경우 "완료" 로 안내됩니다.',
        note: '',
        retry: 'reload',
    };
}
